/**
 * @module Split Section - Image + Content
 * @description Split Section 50/50
 */
import { useEffect, useState } from "react";
import Image from "next/image";
import { fastly } from "@/js/utils/helpers";
import state from "@/js/config/state";
import DynamicButton from "@/blocks/Buttons/DynamicButton";
import PhoneButton from "@/blocks/Buttons/PhoneButton";
import styles from "./index.module.css";
import Tooltip from "@/blocks/Tooltip";

const defaultItems = [
  "Amazon eero Wi-Fi 6E System",
  <>
    FREE voice line (plus taxes and surcharges)
    <span key="span-1" data-legal="free-voice-line" />
  </>,
  <>
    Free installation!!!
    <Tooltip disclosure="includes-tech-installation" />
  </>,
];

const defaultImage = {
  url: "/pages/home/split-section-720x514.jpg",
  blurUrl: "/pages/home/split-section-720x514.jpg",
  alt: "Woman wearing VR - 2G text",
  className: "",
};

const position = "SPLIT SECTION";

/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  const {
    title = <>Headline</>,
    subtitle = null,
    content,
    showItems,
    items = defaultItems,
    legal = "",
    dualCTA,
    theme = null,
    reverseLayout = false,
    reverseMobile,
    componentList = null,
    customStyles,
    objectFit = "cover",
    renderPhoneCta = false,
  } = props;

  let { image = defaultImage } = props;
  image = { ...image, defaultImage };

  const [hideDesktopBackground, setHideDesktopBackground] = useState(true);

  const determineViewport = () => {
    if (!state.is_mobile) {
      return setHideDesktopBackground(false);
    }

    return setHideDesktopBackground(true);
  };

  useEffect(() => {
    determineViewport();
    document.addEventListener("redventures/resize_executed", determineViewport);
  }, []);

  return (
    <section
      className={`component ${styles.component} ${reverseMobile && styles.componentMobileReverse} ${theme && `u-theme-${theme}`}`}
    >
      <div
        className={`${styles.component__backgroundWrap} ${styles.component__backgroundWrapMobile} ${objectFit === "contain" && styles.component__backgroundWrapContain}`}
      >
        <div
          className={`
          ${styles.component__background}
          ${styles.component__backgroundMobile}
          ${customStyles && customStyles.component__backgroundMobile} `}
        >
          <Image
            alt={image.alt}
            src={image.url}
            loader={fastly}
            className={`component__background_img ${image.className}`}
            loading="lazy"
            width={320}
            height={230}
          />
        </div>
        {/* <!-- .component__background --> */}
      </div>

      <div
        className={`l-container ${customStyles && customStyles.component__layoutContainer}`}
      >
        <div
          className={`g-row g-row--match-height ${!reverseLayout && "g-row--reorder-2-col"}`}
        >
          <div
            className={`g-col g-col--one-half ${customStyles && customStyles.component__half_content}`}
          >
            <div
              className={`${styles.component__content} ${reverseLayout && styles.component__contentReverse} ${customStyles && customStyles.component__content}`}
            >
              <h2
                className={`${styles.component__title} ${customStyles && customStyles.component__title}`}
              >
                {title}
              </h2>
              {subtitle && (
                <p
                  className={`h6 ${styles.component__subtitle} ${customStyles && customStyles.component__subtitle}`}
                >
                  {subtitle}
                </p>
              )}

              {content && <p>{content}</p>}

              {showItems !== false ? (
                <ul
                  className={`${styles.component__list} ${customStyles && customStyles.component__list} `}
                >
                  {componentList
                    ? componentList.map(({ headline, subhead }, index) => (
                        <li key={index}>
                          <h3 className="h5">{headline}</h3>
                          <p>{subhead}</p>
                        </li>
                      ))
                    : items
                      ? items.map((item, index) => (
                          <li key={index}>
                            <span
                              className={`icon ${renderPhoneCta !== true ? "icon-checkmark1" : "icon-checkmark2"} ${styles.component__icon} ${theme && styles[`component__icon__${theme}`]}`}
                            />
                            {item}
                          </li>
                        ))
                      : ""}
                </ul>
              ) : (
                ""
              )}
              {legal && (
                <p className={`legal ${styles.component__legal}`}>{legal}</p>
              )}

              {renderPhoneCta !== true ? (
                <DynamicButton
                  className={`btn ${dualCTA && styles.component__buttonStyle} ${customStyles && customStyles.component__buttonStyle}`}
                  type="cart"
                  position={position}
                />
              ) : (
                <PhoneButton
                  className={`btn ${customStyles && customStyles.component__buttonStyle}`}
                  positon={position}
                />
              )}

              {dualCTA && (
                <DynamicButton
                  className={`btn btn--blackOutline ${styles.component__buttonStyle}`}
                  href={dualCTA}
                  position={position}
                >
                  Learn More
                </DynamicButton>
              )}
            </div>
            {/* <!-- .component__content --> */}
          </div>
          {/* <!-- .g-col --> */}

          <div
            className={`g-col g-col--one-half ${customStyles && customStyles.component__half_image}`}
          >
            {!hideDesktopBackground && (
              <div
                className={`${styles.component__backgroundWrap} ${reverseLayout && styles.component__backgroundWrapReverse} ${customStyles && customStyles.component__backgroundWrap}`}
              >
                <div
                  className={`${styles.component__background} ${customStyles && customStyles.component__background}`}
                >
                  <Image
                    alt={image.alt}
                    src={image.url}
                    className={`component__background_img ${image.className}`}
                    loader={fastly}
                    loading="lazy"
                    width={720}
                    height={582}
                  />
                </div>
              </div>
            )}
          </div>
          {/* <!-- .g-col --> */}
        </div>
        {/* <!-- .g-row --> */}
      </div>
    </section>
  );
};

export default Component;
